import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  ReactFlowProvider,
  MarkerType,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import dagre from "dagre";

import { api } from "@/lib/api";
import EmployeeNode from "@/components/EmployeeNode";
import EmployeeDrawer from "@/components/EmployeeDrawer";
import MultiSelect from "@/components/MultiSelect";
import ExportMenu from "@/components/ExportMenu";
import ShareDialog from "@/components/ShareDialog";
import BulkImportDialog from "@/components/BulkImportDialog";
import TimeTravelButton from "@/components/TimeTravelButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, RefreshCw, UploadCloud, FileX, Filter, X, Share2, FileUp, GitBranch, Clock, GitMerge, GitCompare } from "lucide-react";
import ScenarioDiffDialog from "@/components/ScenarioDiffDialog";

const NODE_W = 240;
const NODE_H = 96;

const nodeTypes = { employee: EmployeeNode };

function layoutGraph(nodes, edges, direction = "TB") {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: direction, nodesep: 40, ranksep: 80 });
  nodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const p = g.node(n.id);
    return { ...n, position: { x: p.x - NODE_W / 2, y: p.y - NODE_H / 2 }, targetPosition: "top", sourcePosition: "bottom" };
  });
}

function matches(emp, filters) {
  if (filters.departments.length > 0 && !filters.departments.includes(emp.department || "")) return false;
  if (filters.designations.length > 0 && !filters.designations.includes(emp.designation || "")) return false;
  return true;
}

function buildGraph(employees, onSelect, filters) {
  const matchedIds = new Set(employees.filter((e) => matches(e, filters)).map((e) => e.id));
  const filtering = filters.departments.length > 0 || filters.designations.length > 0;
  const nodes = employees.map((e) => {
    const isMatch = matchedIds.has(e.id);
    return {
      id: e.id,
      type: "employee",
      position: { x: 0, y: 0 },
      data: { employee: e, onSelect, dimmed: filtering && !isMatch },
      style: filtering && !isMatch ? { opacity: 0.18 } : { opacity: 1 },
    };
  });
  const edges = employees
    .filter((e) => e.manager_id)
    .map((e) => {
      const bothMatch = matchedIds.has(e.id) && matchedIds.has(e.manager_id);
      const dim = filtering && !bothMatch;
      return {
        id: `e-${e.manager_id}-${e.id}`,
        source: e.manager_id,
        target: e.id,
        type: "smoothstep",
        style: { stroke: dim ? "#D4D4D2" : "#111", strokeWidth: 1, opacity: dim ? 0.45 : 1 },
        markerEnd: { type: MarkerType.ArrowClosed, color: dim ? "#D4D4D2" : "#111", width: 14, height: 14 },
      };
    });
  return { nodes: layoutGraph(nodes, edges), edges, matchedCount: matchedIds.size };
}

function OrganogramInner() {
  const [employees, setEmployees] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", designation: "", department: "", email: "", manager_id: "none" });
  const [filters, setFilters] = useState({ departments: [], designations: [] });
  const [shareOpen, setShareOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [companyName, setCompanyName] = useState("Organogram");
  const flowWrapperRef = useRef(null);
  const reactFlowInstance = useReactFlow();

  const [searchParams, setSearchParams] = useSearchParams();
  const scenarioId = searchParams.get("scenario") || null;
  const [scenario, setScenario] = useState(null);
  const [asOf, setAsOf] = useState(null); // ISO string when in time-travel mode
  const [diffOpen, setDiffOpen] = useState(false);

  const readOnly = !!asOf || (scenario && scenario.status !== "draft");
  const liveMode = !asOf && !scenarioId;

  const fetchEmployees = useCallback(async () => {
    if (asOf) {
      const { data } = await api.get(`/employees/at?at=${encodeURIComponent(asOf)}`);
      setEmployees(data.employees);
      return;
    }
    const { data } = await api.get("/employees");
    if (scenarioId) {
      // Apply scenario overrides, additions and removals client-side
      try {
        const { data: scen } = await api.get(`/scenarios/${scenarioId}`);
        setScenario(scen);
        const removed = new Set(scen.removed || []);
        const merged = data
          .filter((e) => !removed.has(e.id))
          .map((e) => {
            const ov = (scen.overrides || {})[e.id];
            return ov ? { ...e, ...ov, _scenarioChanged: true } : e;
          });
        const added = (scen.added || []).map((e) => ({ ...e, _scenarioAdded: true }));
        setEmployees([...merged, ...added]);
        return;
      } catch (e) {
        toast.error("Scenario not found");
        setScenario(null);
        setSearchParams({});
      }
    } else {
      setScenario(null);
    }
    setEmployees(data);
  }, [asOf, scenarioId, setSearchParams]);

  useEffect(() => {
    fetchEmployees();
    api.get("/settings").then((r) => setCompanyName(r.data?.company_name || "Organogram")).catch(() => {});
  }, [fetchEmployees]);

  const exitScenario = () => {
    setScenario(null);
    setSearchParams({});
  };

  const mergeScenario = async () => {
    if (!scenario) return;
    if (!window.confirm(`Apply all ${Object.keys(scenario.overrides || {}).length} changes from "${scenario.name}" to the live org chart?`)) return;
    try {
      const { data } = await api.post(`/scenarios/${scenario.id}/merge`);
      toast.success(`Merged — ${data.applied} employees updated`);
      exitScenario();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    }
  };

  const handleSelect = useCallback((emp) => setSelected(emp), []);

  const departments = useMemo(
    () => Array.from(new Set(employees.map((e) => e.department).filter(Boolean))).sort(),
    [employees],
  );
  const designations = useMemo(
    () => Array.from(new Set(employees.map((e) => e.designation).filter(Boolean))).sort(),
    [employees],
  );

  const { matchedCount, filtering } = useMemo(() => {
    const f = filters.departments.length > 0 || filters.designations.length > 0;
    const c = employees.filter((e) => matches(e, filters)).length;
    return { matchedCount: c, filtering: f };
  }, [employees, filters]);

  useEffect(() => {
    const { nodes: n, edges: e } = buildGraph(employees, handleSelect, filters);
    setNodes(n);
    setEdges(e);
  }, [employees, handleSelect, setNodes, setEdges, filters]);

  const clearFilters = () => setFilters({ departments: [], designations: [] });

  const handleSync = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/microsoft/sync");
      toast.success(`Synced ${data.synced} users from Microsoft${data.mocked ? " (mock)" : ""}`);
      await fetchEmployees();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sync failed", { duration: 8000 });
    }
    setLoading(false);
  };

  const handlePush = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/microsoft/push");
      const errCount = data.errors?.length || 0;
      if (errCount) {
        toast.warning(`Pushed ${data.pushed}, ${errCount} failed`, { duration: 8000 });
      } else {
        toast.success(`Pushed ${data.pushed} changes${data.mocked ? " (mock)" : ""}`);
      }
      await fetchEmployees();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Push failed", { duration: 8000 });
    }
    setLoading(false);
  };

  const handleClear = async () => {
    if (!window.confirm("Delete ALL employees and start from blank?")) return;
    try {
      await api.post("/employees/clear");
      toast.success("Cleared all employees");
      setSelected(null);
      await fetchEmployees();
    } catch {
      toast.error("Clear failed");
    }
  };

  const handleAdd = async () => {
    if (!addForm.name.trim()) {
      toast.error("Name required");
      return;
    }
    try {
      const payload = { ...addForm, manager_id: addForm.manager_id === "none" ? null : addForm.manager_id };
      if (scenarioId) {
        const { data } = await api.post(`/scenarios/${scenarioId}/add`, payload);
        toast.success("Added to scenario");
        await fetchEmployees();
      } else {
        const { data } = await api.post("/employees", payload);
        toast.success("Employee added");
        setEmployees((es) => [...es, data]);
      }
      setShowAdd(false);
      setAddForm({ name: "", designation: "", department: "", email: "", manager_id: "none" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };

  const onConnect = useCallback(
    async (conn) => {
      // re-parenting: target's manager becomes source
      try {
        await api.put(`/employees/${conn.target}`, { manager_id: conn.source });
        setEdges((es) => addEdge({ ...conn, type: "smoothstep" }, es));
        await fetchEmployees();
        toast.success("Reporting line updated");
      } catch {
        toast.error("Update failed");
      }
    },
    [setEdges, fetchEmployees],
  );

  const onSaved = async () => {
    await fetchEmployees();
  };
  const onDeleted = async (id) => {
    setSelected(null);
    setEmployees((es) => es.filter((e) => e.id !== id));
    await fetchEmployees();
  };

  const counts = useMemo(() => {
    const total = employees.length;
    const ms = employees.filter((e) => e.source === "microsoft").length;
    const pending = employees.filter((e) => e.sync_status && e.sync_status.startsWith("pending")).length;
    return { total, ms, pending };
  }, [employees]);

  return (
    <div className="flex h-screen flex-col">
      {/* Toolbar */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-6 py-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            Visual Hierarchy
          </p>
          <h1 className="text-2xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
            Organogram
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="mr-4 hidden gap-4 md:flex">
            <Stat label="Total" value={counts.total} />
            <Stat label="From MS" value={counts.ms} />
            <Stat label="Pending" value={counts.pending} highlight={counts.pending > 0} />
          </div>
          <Button
            variant="outline"
            onClick={() => setImportOpen(true)}
            className="rounded-none border-neutral-300"
            data-testid="bulk-import-button"
            disabled={readOnly}
          >
            <FileUp size={14} className="mr-2"/>
            Import CSV
          </Button>
          <TimeTravelButton asOf={asOf} onChange={setAsOf}/>
          <ExportMenu
            reactFlowInstance={reactFlowInstance}
            getNode={() => flowWrapperRef.current?.querySelector(".react-flow")}
            companyName={companyName}
          />
          <Button
            variant="outline"
            onClick={() => setShareOpen(true)}
            className="rounded-none border-neutral-300"
            data-testid="share-button"
          >
            <Share2 size={14} className="mr-2"/>
            Share
          </Button>
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={loading}
            className="rounded-none border-neutral-300"
            data-testid="ms-sync-button"
          >
            <RefreshCw size={14} className="mr-2" />
            Sync from Microsoft
          </Button>
          <Button
            variant="outline"
            onClick={handlePush}
            disabled={loading || counts.pending === 0}
            className="rounded-none border-neutral-300"
            data-testid="ms-push-button"
          >
            <UploadCloud size={14} className="mr-2" />
            Push to Microsoft
          </Button>
          <Button
            variant="outline"
            onClick={handleClear}
            className="rounded-none border-neutral-300 text-[#C50F1F] hover:bg-red-50"
            data-testid="build-blank-button"
          >
            <FileX size={14} className="mr-2" />
            Build from blank
          </Button>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]" data-testid="add-employee-button">
                <Plus size={14} className="mr-2" />
                Add employee
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: '"Work Sans", sans-serif' }}>Add employee</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <FormRow label="Name">
                  <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="rounded-none" data-testid="add-name-input" />
                </FormRow>
                <FormRow label="Designation">
                  <Input value={addForm.designation} onChange={(e) => setAddForm({ ...addForm, designation: e.target.value })} className="rounded-none" data-testid="add-designation-input" />
                </FormRow>
                <FormRow label="Department">
                  <Input value={addForm.department} onChange={(e) => setAddForm({ ...addForm, department: e.target.value })} className="rounded-none" data-testid="add-department-input" />
                </FormRow>
                <FormRow label="Email">
                  <Input value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} className="rounded-none" data-testid="add-email-input" />
                </FormRow>
                <FormRow label="Reports to">
                  <Select value={addForm.manager_id} onValueChange={(v) => setAddForm({ ...addForm, manager_id: v })}>
                    <SelectTrigger className="rounded-none" data-testid="add-manager-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None (Root) —</SelectItem>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormRow>
              </div>
              <DialogFooter>
                <Button onClick={handleAdd} className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]" data-testid="add-employee-submit">
                  Add
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Time travel / Scenario banner */}
      {(asOf || scenario) && (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 border-b px-6 py-2 ${
            scenario ? "border-[#5B5FC7] bg-[#EEF0FE]" : "border-[#D83B01] bg-orange-50"
          }`}
          data-testid="mode-banner"
        >
          <div className="flex items-center gap-2 text-xs">
            {scenario ? (
              <>
                <GitBranch size={14} className="text-[#5B5FC7]"/>
                <span className="font-medium" style={{ fontFamily: '"Work Sans", sans-serif' }}>Scenario:</span>
                <span>{scenario.name}</span>
                <span className="text-neutral-500">·</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-600">
                  {Object.keys(scenario.overrides || {}).length} changes · status: {scenario.status}
                </span>
              </>
            ) : (
              <>
                <Clock size={14} className="text-[#D83B01]"/>
                <span className="font-medium" style={{ fontFamily: '"Work Sans", sans-serif' }}>Time travel:</span>
                <span>viewing as of {new Date(asOf).toLocaleString()}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#D83B01]">Read only</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {scenario && scenario.status === "draft" && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDiffOpen(true)}
                  className="rounded-none border-[#5B5FC7] text-[#5B5FC7] hover:bg-[#5B5FC7] hover:text-white"
                  data-testid="scenario-diff-button"
                >
                  <GitCompare size={12} className="mr-1.5"/>
                  View diff
                </Button>
                <Button
                  size="sm"
                  onClick={mergeScenario}
                  disabled={Object.keys(scenario.overrides || {}).length + (scenario.added || []).length + (scenario.removed || []).length === 0}
                  className="rounded-none bg-[#0A7029] text-xs text-white hover:bg-[#085A22]"
                  data-testid="banner-merge-button"
                >
                  <GitMerge size={12} className="mr-1.5"/>
                  Merge to live
                </Button>
              </>
            )}
            <button
              onClick={scenario ? exitScenario : () => setAsOf(null)}
              className="flex items-center gap-1 border border-neutral-300 bg-white px-2 py-1 text-[10px] uppercase tracking-wider text-neutral-700 hover:border-black"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
              data-testid="exit-mode-button"
            >
              <X size={11}/>
              {scenario ? "Exit scenario" : "Back to today"}
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div
        className="flex flex-wrap items-center gap-3 border-b border-neutral-200 bg-white px-6 py-3"
        data-testid="organogram-filter-bar"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          <Filter size={13} />
          Filter
        </div>
        <MultiSelect
          options={departments}
          value={filters.departments}
          onChange={(v) => setFilters((f) => ({ ...f, departments: v }))}
          label="All departments"
          placeholder="Departments"
          width="w-[220px]"
          testId="filter-department-select"
        />
        <MultiSelect
          options={designations}
          value={filters.designations}
          onChange={(v) => setFilters((f) => ({ ...f, designations: v }))}
          label="All designations"
          placeholder="Designations"
          width="w-[240px]"
          testId="filter-designation-select"
        />
        {filtering && (
          <>
            <button
              onClick={clearFilters}
              className="flex h-8 items-center gap-1 border border-neutral-300 px-2 text-[11px] uppercase tracking-wider text-neutral-700 hover:border-black hover:text-black"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
              data-testid="clear-filters-button"
            >
              <X size={11}/>
              Clear all
            </button>
            <span className="text-[11px] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              <span className="text-black">{matchedCount}</span> / {employees.length} highlighted
            </span>
          </>
        )}
      </div>

      {/* Canvas */}
      <div ref={flowWrapperRef} className="relative flex-1 bg-[#F4F4F2]" data-testid="organogram-canvas">
        {employees.length === 0 ? (
          <EmptyState onSync={handleSync} onAdd={() => setShowAdd(true)} />
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} color="#D4D4D2" />
            <Controls className="!rounded-none !border-neutral-300 !shadow-none [&_button]:!rounded-none" />
            <MiniMap pannable className="!border !border-neutral-300" maskColor="rgba(244,244,242,0.6)" nodeColor="#002FA7" />
          </ReactFlow>
        )}
      </div>

      <EmployeeDrawer
        employee={selected}
        employees={employees}
        open={!!selected}
        onClose={() => setSelected(null)}
        onSaved={onSaved}
        onDeleted={onDeleted}
        scenarioId={scenarioId}
        readOnly={!!asOf}
      />

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen}/>
      <BulkImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={fetchEmployees}/>
      <ScenarioDiffDialog scenarioId={scenarioId} open={diffOpen} onOpenChange={setDiffOpen}/>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
        {label}
      </p>
      <p className={`text-xl tracking-tight ${highlight ? "text-[#D83B01]" : ""}`} style={{ fontFamily: '"Work Sans", sans-serif' }}>
        {value}
      </p>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</Label>
      {children}
    </div>
  );
}

function EmptyState({ onSync, onAdd }) {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-md border border-dashed border-neutral-300 bg-white p-10 text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          Empty Canvas
        </p>
        <h3 className="mt-2 text-2xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
          No one's here yet.
        </h3>
        <p className="mt-2 text-sm text-neutral-600">
          Pull your team from Microsoft 365, or start sketching the structure by hand.
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Button onClick={onSync} className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]" data-testid="empty-sync-button">
            <RefreshCw size={14} className="mr-2" />
            Sync from Microsoft
          </Button>
          <Button variant="outline" onClick={onAdd} className="rounded-none border-neutral-300" data-testid="empty-add-button">
            <Plus size={14} className="mr-2" />
            Add manually
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Organogram() {
  return (
    <ReactFlowProvider>
      <OrganogramInner />
    </ReactFlowProvider>
  );
}
